/**
 * BillingPage - License management
 * 
 * Two-Tier Model:
 * - STANDARD ($99 AUD one-time) - Full features for SMB
 * - ENTERPRISE (Contact Sales) - Tailored solutions
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useFeatures } from '../hooks/useFeatures';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { Check, Zap, Loader2, Crown, ArrowRight, Key, Building, Download } from 'lucide-react';
import EnterpriseContactModal from '../components/EnterpriseContactModal';
import '../components/EnterpriseContactModal.css';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const BillingPage = ({ onBack, onNavigate }) => {
  const { getAccessToken } = useAuth();
  const { tier, tierName, limits, usage, refetch: refetchFeatures, has } = useFeatures();
  
  const [billingStatus, setBillingStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [showEnterpriseModal, setShowEnterpriseModal] = useState(false);

  // Check for return from Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const status = params.get('status');

    if (sessionId && status === 'success') {
      pollPaymentStatus(sessionId);
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (status === 'cancelled') {
      toast.info('Payment cancelled');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Fetch billing status
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = getAccessToken();

        // Fetch billing status (requires auth)
        const statusRes = await fetch(`${API}/billing/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (statusRes.ok) {
          const statusData = await statusRes.json();
          setBillingStatus(statusData);
        }
      } catch (err) {
        console.error('Error fetching billing data:', err);
        toast.error('Failed to load billing information');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [getAccessToken]);

  // Poll payment status
  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment status check timed out. Please refresh the page.');
      return;
    }

    try {
      const token = getAccessToken();
      const res = await fetch(`${API}/billing/checkout/status/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Failed to check payment status');

      const data = await res.json();

      if (data.payment_status === 'paid' || data.tier_updated) {
        toast.success('Purchase complete! Your access has been activated.');
        refetchFeatures();
        // Reload billing status
        const statusRes = await fetch(`${API}/billing/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (statusRes.ok) {
          setBillingStatus(await statusRes.json());
        }
        return;
      }

      if (data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        return;
      }

      // Continue polling
      toast.loading('Processing payment...', { id: 'payment-processing' });
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      toast.error('Error checking payment status');
    }
  }, [getAccessToken, refetchFeatures]);

  // Handle checkout
  const handleCheckout = async () => {
    setCheckoutLoading(true);
    
    try {
      const token = getAccessToken();
      const originUrl = window.location.origin;

      const res = await fetch(`${API}/billing/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          origin_url: originUrl
        })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || 'Failed to create checkout session');
      }

      // Redirect to Stripe
      window.location.href = data.checkout_url;
    } catch (err) {
      console.error('Checkout error:', err);
      toast.error(err.message || 'Failed to start checkout');
      setCheckoutLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="billing-page-loading">
        <Loader2 className="animate-spin" size={32} />
        <p>Loading billing information...</p>
      </div>
    );
  }

  const isPurchased = tier === 'STANDARD' || tier === 'PRO' || tier === 'ENTERPRISE';
  const isEnterprise = tier === 'ENTERPRISE';

  return (
    <div className="page billing-page" data-testid="billing-page">
      <div className="page-header">
        <div>
          <h1>Billing</h1>
          <p>Manage your RISKEE license</p>
        </div>
        {onBack && (
          <Button variant="outline" onClick={onBack}>
            ← Back to Dashboard
          </Button>
        )}
      </div>

      {/* Current Plan Card */}
      <Card className="current-plan-card" data-testid="current-plan">
        <div className="current-plan-header">
          <div className="current-plan-badge">
            <Crown size={20} />
            <span>Current Status</span>
          </div>
          <h2>{isEnterprise ? 'Enterprise' : isPurchased ? 'RISKEE Standard' : 'Trial'}</h2>
        </div>
        
        <div className="current-plan-details">
          <div className="usage-stats">
            <div className="usage-stat">
              <span className="usage-label">Users</span>
              <span className="usage-value">
                {usage?.users || 0} / {limits?.max_users === -1 ? '∞' : limits?.max_users || 10}
              </span>
            </div>
            <div className="usage-stat">
              <span className="usage-label">Sites</span>
              <span className="usage-value">
                {usage?.sites || 0} / {limits?.max_sites === -1 ? '∞' : limits?.max_sites || 3}
              </span>
            </div>
            <div className="usage-stat">
              <span className="usage-label">Reports/Month</span>
              <span className="usage-value">
                {usage?.risks_this_month || 0} / {limits?.max_risks_per_month === -1 ? '∞' : limits?.max_risks_per_month || 500}
              </span>
            </div>
          </div>
          
          {isPurchased && billingStatus?.purchase_date && (
            <p className="subscription-info">
              Purchased: {billingStatus.purchase_date}
            </p>
          )}
        </div>
      </Card>

      {/* Downloads Section (for purchased users) */}
      {isPurchased && (
        <Card className="downloads-link-card" data-testid="downloads-link">
          <button 
            className="feature-link"
            onClick={() => onNavigate?.('settings/downloads')}
            data-testid="downloads-link-btn"
          >
            <Download size={20} />
            <div>
              <strong>Downloads</strong>
              <span>Self-hosted package, guides, and release notes</span>
            </div>
            <ArrowRight size={18} />
          </button>
        </Card>
      )}

      {/* Enterprise Features Section */}
      {has('api_access') && (
        <Card className="enterprise-features-card" data-testid="enterprise-features">
          <h3>Enterprise Features</h3>
          <div className="enterprise-feature-links">
            <button 
              className="feature-link"
              onClick={() => onNavigate?.('settings/api-keys')}
              data-testid="api-keys-link"
            >
              <Key size={20} />
              <div>
                <strong>API Keys</strong>
                <span>Manage programmatic access</span>
              </div>
              <ArrowRight size={18} />
            </button>
          </div>
        </Card>
      )}

      {/* Purchase Card (only show if not already purchased) */}
      {!isPurchased && (
        <Card className="pricing-card-single" data-testid="purchase-card">
          <div className="pricing-header-single">
            <h3>RISKEE Standard</h3>
            <div className="pricing-amount-single">
              <span className="currency">$</span>
              <span className="amount">99</span>
              <span className="currency-label">AUD</span>
            </div>
            <p className="pricing-type">One-time purchase</p>
          </div>

          <ul className="pricing-features-single">
            <li><Check size={16} /> Up to 10 users</li>
            <li><Check size={16} /> 3 site locations</li>
            <li><Check size={16} /> Full audit logs</li>
            <li><Check size={16} /> Workplace inspections</li>
            <li><Check size={16} /> Advanced reporting</li>
            <li><Check size={16} /> Bulk export</li>
          </ul>

          <div className="pricing-values">
            <span>No subscription.</span>
            <span>No monthly fees.</span>
            <span>No recurring charges.</span>
          </div>

          <Button 
            size="lg"
            className="purchase-btn"
            onClick={handleCheckout}
            disabled={checkoutLoading}
            data-testid="purchase-btn"
          >
            {checkoutLoading ? (
              <><Loader2 className="animate-spin" size={18} /> Processing...</>
            ) : (
              <><Zap size={18} /> Purchase RISKEE</>
            )}
          </Button>
        </Card>
      )}

      {/* Enterprise Contact Card */}
      {isPurchased && !isEnterprise && (
        <Card className="enterprise-contact-card">
          <div className="enterprise-contact-content">
            <Building size={28} />
            <h3>Need More?</h3>
            <p>Unlimited users, sites, API access, SSO, and dedicated support.</p>
            <p className="enterprise-tagline">Tailored solutions. One-off payment or support program.</p>
            <Button 
              variant="outline"
              onClick={() => setShowEnterpriseModal(true)}
              data-testid="contact-sales-btn"
            >
              <Building size={16} />
              Contact Sales
            </Button>
          </div>
        </Card>
      )}

      {/* Already purchased message */}
      {isPurchased && (
        <Card className="purchased-card">
          <div className="purchased-content">
            <Check size={32} className="purchased-icon" />
            <h3>{isEnterprise ? 'Enterprise License Active' : 'RISKEE Standard Active'}</h3>
            <p>You have full access to all {isEnterprise ? 'enterprise' : 'standard'} features.</p>
          </div>
        </Card>
      )}

      {/* Enterprise Contact Modal */}
      <EnterpriseContactModal 
        isOpen={showEnterpriseModal} 
        onClose={() => setShowEnterpriseModal(false)} 
      />
    </div>
  );
};

export default BillingPage;
